/**
 * Optimistic-cache helper for TanStack mutation hooks (BUG21).
 *
 * Every server-backed instant-feedback control in Settings renders from a
 * TanStack query and writes through a mutation hook. Before this helper the
 * hooks only invalidated on success, so the rendered control didn't move until
 * the PATCH resolved *and* the forced refetch returned — two requests in series,
 * ~1s on a real mobile connection (the BUG17 class, generalized to the hook
 * layer). See `docs/boards/dev/BUG21-settings-controls-not-optimistic.md`.
 *
 * This factory builds the standard TanStack optimistic trio so each hook opts in
 * with one call and rollback/snapshot is uniform:
 *
 *   - `onMutate(vars)` cancels in-flight queries for the key, snapshots the
 *     cache, applies the optimistic `setQueryData`, and returns the snapshot as
 *     rollback context.
 *   - `onError(_e, _vars, ctx)` restores the snapshot.
 *   - `onSettled` invalidates the same key(s) the hook reconciles with today —
 *     now a background refetch that no longer gates the visible change.
 *
 * Field-mapping gotcha (read before writing an `updater`): the update *request*
 * is camelCase while the cached *row* is snake_case. The `updater` must map
 * every request key to its row key, never blind-spread the patch — an unmapped
 * key writes a field the component never reads and that control stays laggy.
 *
 * Composition gotcha (steppers): `onMutate` `await`s `cancelQueries` before
 * `setQueryData`, so a second tap firing inside that window would read the
 * pre-write cache. Read the latest cached value inside the `updater` (or apply a
 * delta) so `0 → + → +` lands on 2, not a lost update.
 */

import type { QueryClient, QueryKey } from '@tanstack/react-query';

/**
 * The rollback context returned from `onMutate` and consumed by `onError`.
 * Holds the pre-mutation snapshot of the optimistic key.
 */
export interface OptimisticContext<TData> {
  snapshot: TData | undefined;
}

export interface OptimisticCacheUpdateConfig<TData, TVars> {
  queryClient: QueryClient;
  /** The query key whose cache the optimistic write targets. */
  queryKey: QueryKey;
  /**
   * Pure transform from the current cached value (+ mutation vars) to the next
   * cached value. Receives the freshest cache value (read inside `onMutate`
   * after `cancelQueries`), so composing rapid taps is correct.
   */
  updater: (old: TData | undefined, vars: TVars) => TData | undefined;
  /**
   * Keys to invalidate on settle. Defaults to `[queryKey]`. Pass extra keys
   * when the write reconciles authoritative/server-computed fields on other
   * queries (e.g. a cadence write also refreshes the product detail + list).
   */
  invalidateKeys?: QueryKey[];
  /**
   * Optional side effect run after the settle invalidations — e.g. T12.5.1 (G)
   * broadcasts a cross-tab preference-changed signal so other tabs refresh their
   * badge queries. Runs on every settle (success or error), after invalidation.
   */
  afterSettle?: () => void;
}

export interface OptimisticTrio<TData, TVars> {
  onMutate: (vars: TVars) => Promise<OptimisticContext<TData>>;
  onError: (
    error: unknown,
    vars: TVars,
    context: OptimisticContext<TData> | undefined
  ) => void;
  onSettled: () => void;
}

/**
 * Build the `{ onMutate, onError, onSettled }` trio for a `useMutation` call.
 * Spread it into the mutation options alongside `mutationFn`.
 */
export function makeOptimisticCacheUpdate<TData, TVars>(
  config: OptimisticCacheUpdateConfig<TData, TVars>
): OptimisticTrio<TData, TVars> {
  const { queryClient: qc, queryKey, updater, afterSettle } = config;
  const invalidateKeys = config.invalidateKeys ?? [queryKey];

  return {
    async onMutate(vars) {
      // Cancel any in-flight refetch so a landing GET can't clobber the
      // optimistic write we're about to apply.
      await qc.cancelQueries({ queryKey });
      const snapshot = qc.getQueryData<TData>(queryKey);
      // Read the freshest cache inside the updater (passed as `old`) so rapid
      // taps compose instead of each sending the same pre-write absolute value.
      qc.setQueryData<TData>(queryKey, (old) => updater(old, vars));
      return { snapshot };
    },
    onError(_error, _vars, context) {
      if (context) {
        qc.setQueryData<TData>(queryKey, context.snapshot);
      }
    },
    onSettled() {
      for (const key of invalidateKeys) {
        void qc.invalidateQueries({ queryKey: key });
      }
      afterSettle?.();
    },
  };
}
